import type { VFC } from 'react'
import { unversioned } from '@opallabs/api'
import { Size } from '@opallabs/gem'
import { ChildrenWithInjectedProps } from '@opallabs/lambda'
import { Button, ButtonIconPosition, Icon } from '@opallabs/lapidary'
import {
  ViewHeader,
  ViewHeaderControl,
  ViewHeaderNavigator,
} from '@opallabs/lapidary-unstable'
import type { ViewHeaderActionMenuOption } from '@opallabs/lapidary-unstable'
import { testFeature } from '@opallabs/principles'
import { showPptxToast, trackPptxLaunch } from '../../Editor/pptxExportHelpers'
import { HeaderProps } from './types'

export const Header: VFC<HeaderProps> = ({
  currentIndex,
  defaultTitle,
  onClickDelete,
  onClickExportPdf,
  onClickNext,
  onClickPlay,
  onClickPrevious,
  onClickShare,
  onNavigateToParent,
  onToggleSettings,
  parentTitle,
  presentationId,
  showSettings = false,
  theme = 'light',
  title,
  totalCount = 1,
}) => {
  const hasSlides = totalCount > 0
  const hasNext = hasSlides && currentIndex + 1 < totalCount
  const hasPrevious = hasSlides && currentIndex > 0

  const onClickExportPptx = async () => {
    trackPptxLaunch()

    try {
      await unversioned.createPptxExport({ id: presentationId })
      showPptxToast(
        'Exporting to PowerPoint',
        "When it's ready, it will be in Downloads in the Personal Panel and emailed to you.",
        'success'
      )
    } catch {
      showPptxToast(
        'PowerPoint Export Failed',
        'We could not start your export. Please try again.',
        'error'
      )
    }
  }

  const options: ViewHeaderActionMenuOption[] = []
  if (testFeature('export_capabilities')) {
    options.push({ onClick: onClickExportPdf, title: 'Export to PDF' })
    if (testFeature('pptx_export')) {
      options.push({
        onClick: onClickExportPptx,
        title: 'Export to PowerPoint',
      })
    }
  }

  return (
    <ViewHeader
      actionsChildren={
        <ChildrenWithInjectedProps>
          <Button
            buttonText="Share"
            variant="secondary"
            icon={
              <Icon
                ariaHidden={true}
                height={Size.Icon}
                glyph="gem-share"
                width={Size.Icon}
              />
            }
            iconPosition={ButtonIconPosition.leading}
            name="share-button"
            onClick={onClickShare}
          />

          <Button
            buttonText="Play"
            variant="primary"
            icon={
              <Icon
                ariaHidden={true}
                height={Size.Icon}
                glyph="gem-play"
                width={Size.Icon}
              />
            }
            iconPosition={ButtonIconPosition.leading}
            name="play-button"
            onClick={onClickPlay}
          />
        </ChildrenWithInjectedProps>
      }
      actionMenuOptions={options}
      controlsChildren={[
        <ViewHeaderControl
          description="Settings"
          displayLabel={true}
          glyph="gem-settings"
          key="toggle-settings"
          onClick={onToggleSettings}
          pressed={showSettings}
        />,
      ]}
      defaultTitle={defaultTitle}
      navigator={
        <ViewHeaderNavigator
          hasNext={hasNext}
          hasPrevious={hasPrevious}
          nextButtonLabel="Navigate to next slide"
          previousButtonLabel="Navigate to previous slide"
          onClickNext={onClickNext}
          onClickPrevious={onClickPrevious}
          text={`Slide ${currentIndex + 1} of ${totalCount}`}
        />
      }
      onDestroy={onClickDelete}
      onNavigateToParent={onNavigateToParent}
      parentTitle={parentTitle}
      searchable={false}
      theme={theme}
      title={title}
    />
  )
}
Header.displayName = 'Hubble: Header ← SlideEditor'
